import { useEffect, useState, useCallback } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement,
  Title, Tooltip, Legend, ArcElement, Filler,
} from 'chart.js';
import {
  fetchAnalyticsBundle, fetchRealtimeStats, fetchSocialDetail, fetchAdminStats,
  buildExportUrl, SeriesPoint, BreakdownItem, RealtimeStats, BlogStats,
  SocialDetailBundle, SocialPlatformStats,
} from '../lib/api';
ChartJS.register(
  CategoryScale, LinearScale, BarElement, LineElement, PointElement,
  Title, Tooltip, Legend, ArcElement, Filler,
);

const RANGE_CONFIG: { key: string; label: string }[] = [
  { key: '1min',  label: '1 min' },
  { key: '5min',  label: '5 min' },
  { key: '10min', label: '10 min' },
  { key: '30min', label: '30 min' },
  { key: '1h',    label: '1 hr' },
  { key: '4h',    label: '4 hr' },
  { key: '8h',    label: '8 hr' },
  { key: '12h',   label: '12 hr' },
  { key: '24h',   label: '24 hr' },
  { key: '2d',    label: '2 days' },
  { key: '7d',    label: '7 days' },
  { key: '15d',   label: '15 days' },
  { key: '1m',    label: '1 mo' },
  { key: '3m',    label: '3 mo' },
  { key: '6m',    label: '6 mo' },
  { key: '1y',    label: '1 yr' },
  { key: 'all',   label: 'All' },
  { key: 'custom', label: '📅 Custom' },
];

const ACTIVE_WINDOWS = [
  { key: '1min',  label: '1 min' },
  { key: '5min',  label: '5 min' },
  { key: '10min', label: '10 min' },
  { key: '30min', label: '30 min' },
  { key: '1h',    label: '1 hr' },
];

const ENGINE_COLORS: Record<string, string> = {
  Google: '#4285F4', Bing: '#00809D', Yandex: '#FF0000', Yahoo: '#720E9E',
  DuckDuckGo: '#DE5833', Baidu: '#2319DC', Ecosia: '#4CAF50', Brave: '#FB542B',
  Internal: '#94A3B8', Other: '#CBD5E1', Direct: '#64748B',
};
const SOCIAL_COLORS: Record<string, string> = {
  Facebook: '#1877F2', WhatsApp: '#25D366', TikTok: '#010101', Instagram: '#E1306C',
  YouTube: '#FF0000', Quora: '#B92B27', 'Twitter/X': '#1DA1F2', Reddit: '#FF4500',
  LinkedIn: '#0A66C2', Pinterest: '#E60023', Telegram: '#2CA5E0', Snapchat: '#F7CA18',
  Discord: '#5865F2', Threads: '#101010', Bluesky: '#0085FF', VK: '#4C75A3',
  Mastodon: '#6364FF', Tumblr: '#35465C', 'Hacker News': '#FF6600',
  WeChat: '#07C160', Line: '#00C300', Viber: '#7360F2', Mix: '#FF8226',
};
const BROWSER_COLORS: Record<string, string> = {
  Chrome: '#4285F4', Firefox: '#E66000', Safari: '#006CFF', Edge: '#0078D7',
  Opera: '#FF1B2D', 'Samsung Browser': '#1428A0', 'Internet Explorer': '#1EBBEE',
  'Yandex Browser': '#FFCC00', curl: '#073551', Python: '#306998', 'Node.js': '#43853D', Other: '#94A3B8', Unknown: '#CBD5E1',
};
const DEVICE_COLORS: Record<string, string> = { Desktop: '#3B82F6', Mobile: '#10B981', Tablet: '#F59E0B', Bot: '#6B7280', Unknown: '#CBD5E1' };
const RETENTION_COLORS: Record<string, string> = { 'New Visitors': '#3B82F6', Returning: '#10B981' };

function toDatetimeLocal(ts: number) {
  return new Date(ts * 1000).toISOString().slice(0, 16);
}
function fromDatetimeLocal(v: string) {
  return Math.floor(new Date(v).getTime() / 1000);
}

function formatBucketLabel(ts: number, range: string) {
  const d = new Date(ts * 1000);
  const shortRanges = ['1min', '5min', '10min', '30min', '1h', '4h', '8h', '12h', '24h'];
  return shortRanges.includes(range)
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function BreakdownList({ title, items, emptyMsg, colorMap, maxItems = 10 }: {
  title: string; items: BreakdownItem[]; emptyMsg: string;
  colorMap?: Record<string, string>; maxItems?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const displayed = expanded ? items : items.slice(0, maxItems);
  const total = items.reduce((s, i) => s + i.count, 0);
  return (
    <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-lg border border-gray-200/60 p-6 dark:bg-gray-800/80 dark:border-gray-700/60">
      <h2 className="font-semibold text-gray-900 dark:text-gray-100 mb-4 text-sm">{title}</h2>
      {items.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500 italic">{emptyMsg}</p>
      ) : (
        <>
          <ul className="space-y-2.5">
            {displayed.map((item) => {
              const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
              const color = colorMap?.[item.label] || '#3B82F6';
              return (
                <li key={item.label}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="flex items-center gap-2 text-gray-700 dark:text-gray-300 truncate">
                      {colorMap && <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />}
                      {item.label}
                    </span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100 ml-2 whitespace-nowrap">
                      {item.count.toLocaleString()} <span className="text-gray-400 font-normal">({pct}%)</span>
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1.5">
                    <div className="h-1.5 rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
                  </div>
                </li>
              );
            })}
          </ul>
          {items.length > maxItems && (
            <button onClick={() => setExpanded(p => !p)} className="mt-3 text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 font-medium">
              {expanded ? '▲ Show less' : `▼ Show ${items.length - maxItems} more`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

function DonutCard({ title, items, colorMap }: { title: string; items: BreakdownItem[]; colorMap: Record<string, string> }) {
  if (items.length === 0) return null;
  const data = {
    labels: items.map(i => i.label),
    datasets: [{ data: items.map(i => i.count), backgroundColor: items.map(i => colorMap[i.label] || '#CBD5E1'), borderWidth: 2, borderColor: '#fff' }],
  };
  return (
    <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-lg border border-gray-200/60 p-6 flex flex-col items-center dark:bg-gray-800/80 dark:border-gray-700/60">
      <h2 className="font-semibold text-gray-900 dark:text-gray-100 mb-4 self-start text-sm">{title}</h2>
      <div style={{ width: 180, height: 180 }}>
        <Doughnut data={data} options={{ plugins: { legend: { display: false } }, maintainAspectRatio: false }} />
      </div>
      <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1 w-full">
        {items.slice(0, 10).map(item => (
          <li key={item.label} className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 truncate">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: colorMap[item.label] || '#CBD5E1' }} />
            {item.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatCard({ label, value, sub, accent, icon }: { label: string; value: string | number; sub?: string; accent?: string; icon?: string }) {
  return (
    <div className={`bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow border border-gray-200/60 dark:border-gray-700/60 p-4 text-center transition hover:shadow-md${accent ? ` ring-2 ${accent}` : ''}`}>
      {icon && <div className="text-2xl mb-1">{icon}</div>}
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</p>
      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{typeof value === 'number' ? value.toLocaleString() : value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

// ── Mini SVG sparkline ────────────────────────────────────────────────────────
function Sparkline({ series, color }: { series: { count: number }[]; color: string }) {
  if (!series || series.length < 2) return null;
  const max = Math.max(1, ...series.map(p => p.count));
  const w = 64, h = 22, pad = 2;
  const pts = series.map((p, i) => {
    const x = pad + (i / (series.length - 1)) * (w - 2 * pad);
    const y = pad + (h - 2 * pad) - (p.count / max) * (h - 2 * pad);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg width={w} height={h} className="opacity-80 flex-shrink-0">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// ── Social Platform Deep Dive ────────────────────────────────────────────────
function SocialDeepDive({ detail, socialBreakdown, range }: { detail: SocialDetailBundle | null; socialBreakdown: BreakdownItem[]; range: string }) {
  const [selected, setSelected] = useState<string | null>(null);
  const platforms: SocialPlatformStats[] = detail?.platforms || [];
  const granularity = detail?.granularity || 'daily';
  const bucketSize  = detail?.bucketSize  || 86400;
  const hasPlatforms = platforms.length > 0;
  const hasSimple    = socialBreakdown.length > 0;

  const granLabel = granularity === 'daily' ? 'per day' : granularity === 'weekly' ? 'per week' : 'per month';

  const selP = platforms.find(p => p.platform === selected);
  const selColor = selP ? (SOCIAL_COLORS[selP.platform] || '#94A3B8') : '#3B82F6';

  const detailChartData = selP ? {
    labels: selP.series.map(p => {
      const d = new Date(p.bucket * 1000);
      if (granularity === 'daily')   return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
      if (granularity === 'weekly')  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
      return d.toLocaleDateString([], { month: 'short', year: '2-digit' });
    }),
    datasets: [
      { label: 'Total visits', data: selP.series.map(p => p.count),  backgroundColor: `${selColor}88`, borderColor: selColor, borderWidth: 2, borderRadius: 4 },
      { label: 'Unique visitors', data: selP.series.map(p => p.unique), backgroundColor: 'rgba(16,185,129,0.3)', borderColor: '#10B981', borderWidth: 2, borderRadius: 4 },
    ],
  } : null;

  if (!hasPlatforms && !hasSimple) {
    return (
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-lg border border-gray-200/60 dark:border-gray-700/60 p-6">
        <h2 className="font-semibold text-gray-900 dark:text-gray-100 mb-2 text-sm">📱 Social Platform Analytics</h2>
        <p className="text-sm text-gray-400 italic">No social media referrals in this range.</p>
      </div>
    );
  }

  const listItems = hasPlatforms ? platforms.map(p => ({ label: p.platform, count: p.total })) : socialBreakdown;

  return (
    <div className="space-y-4">
      {/* Section header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-bold text-gray-900 dark:text-gray-100 text-base flex items-center gap-2">
          📱 Social Platform Analytics
        </h2>
        {detail && (
          <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-2.5 py-0.5 rounded-full">
            {granularity === 'daily' ? '📅 Daily' : granularity === 'weekly' ? '📅 Weekly' : '📅 Monthly'} breakdown — click a platform for trend
          </span>
        )}
      </div>

      {/* Platform cards grid */}
      {hasPlatforms ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {platforms.map(p => {
            const color   = SOCIAL_COLORS[p.platform] || '#94A3B8';
            const newPct  = p.unique > 0 ? Math.round((p.new_visitors / p.unique) * 100) : 0;
            const isSel   = selected === p.platform;
            return (
              <button
                key={p.platform}
                onClick={() => setSelected(isSel ? null : p.platform)}
                className={`text-left p-3.5 rounded-2xl border transition-all ${isSel ? 'ring-2 ring-blue-500 border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20 shadow-md' : 'bg-white/80 dark:bg-gray-800/80 border-gray-200/60 dark:border-gray-700/60 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600'}`}
              >
                <div className="flex items-center justify-between mb-2 gap-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
                    <span className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate">{p.platform}</span>
                  </div>
                  <Sparkline series={p.series} color={color} />
                </div>
                <p className="text-xl font-bold text-gray-900 dark:text-gray-100 leading-tight">{p.total.toLocaleString()}</p>
                <div className="mt-1 space-y-0.5">
                  <p className="text-xs text-gray-500 dark:text-gray-400">{p.unique.toLocaleString()} unique</p>
                  <div className="flex gap-2 text-xs">
                    <span className="text-emerald-600 dark:text-emerald-400 font-medium">{newPct}% new</span>
                    <span className="text-amber-600 dark:text-amber-400">{100 - newPct}% ret.</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <BreakdownList title="📱 Social Media Traffic" items={socialBreakdown} emptyMsg="No social media referrals." colorMap={SOCIAL_COLORS} />
          <DonutCard title="📱 Social Share" items={socialBreakdown} colorMap={SOCIAL_COLORS} />
        </div>
      )}

      {/* Selected platform detail */}
      {selP && detailChartData && (
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-lg border border-gray-200/60 dark:border-gray-700/60 p-5">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{ background: selColor }} />
              <span className="font-semibold text-gray-900 dark:text-gray-100">{selP.platform}</span>
              <span className="text-xs text-gray-400 dark:text-gray-500">{granLabel} trend</span>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-500">Total: <span className="font-bold text-gray-900 dark:text-gray-100">{selP.total.toLocaleString()}</span></span>
              <span className="text-gray-500">Unique: <span className="font-bold text-blue-600">{selP.unique.toLocaleString()}</span></span>
              <span className="text-gray-500">New: <span className="font-bold text-emerald-600">{selP.new_visitors.toLocaleString()}</span></span>
              <span className="text-gray-500">Returning: <span className="font-bold text-amber-600">{selP.returning_visitors.toLocaleString()}</span></span>
            </div>
          </div>

          {/* Time-series chart */}
          <div style={{ height: 200 }}>
            <Bar data={detailChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top' as const, labels: { boxWidth: 12, font: { size: 11 } } } }, scales: { y: { beginAtZero: true } } }} />
          </div>

          {/* New vs Returning bar */}
          {selP.unique > 0 && (
            <div className="mt-4">
              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                <span>New vs Returning visitors</span>
                <span>{selP.new_visitors.toLocaleString()} new · {selP.returning_visitors.toLocaleString()} returning</span>
              </div>
              <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden flex">
                <div className="h-full bg-emerald-500 rounded-l-full" style={{ width: `${Math.round((selP.new_visitors / selP.unique) * 100)}%` }} />
                <div className="h-full bg-amber-400 rounded-r-full flex-1" />
              </div>
              <div className="flex gap-4 mt-1.5 text-xs">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />New visitors</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" />Returning visitors</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Overall donut + breakdown list */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <BreakdownList title="📱 Platform Breakdown" items={listItems} emptyMsg="No social data." colorMap={SOCIAL_COLORS} />
        <DonutCard title="📱 Social Share" items={listItems} colorMap={SOCIAL_COLORS} />
      </div>
    </div>
  );
}

function AdminAnalytics() {
  // Persist filter selections to localStorage so they survive page refreshes
  const [type, setType] = useState<'email' | 'visit' | 'post_visit'>(
    () => {
      const saved = localStorage.getItem('admin_analytics_type');
      // 'session' and 'api_call' were removed: sessions stopped being written
      // in Oct 2026 and the product has no API keys, so both were always 0.
      return saved === 'email' || saved === 'post_visit' ? saved : 'visit';
    }
  );
  const [range, setRange] = useState(
    () => localStorage.getItem('admin_analytics_range') || '24h'
  );
  const [activeWindow, setActiveWindow] = useState(
    () => localStorage.getItem('admin_analytics_window') || '5min'
  );

  // Custom date range
  const nowTs = Math.floor(Date.now() / 1000);
  const [customFrom, setCustomFrom] = useState(() => toDatetimeLocal(nowTs - 7 * 86400));
  const [customTo, setCustomTo] = useState(() => toDatetimeLocal(nowTs));

  const [loading, setLoading] = useState(true);
  const [series, setSeries] = useState<SeriesPoint[]>([]);
  const [uniqueSeries, setUniqueSeries] = useState<SeriesPoint[]>([]);
  const [totalUnique, setTotalUnique] = useState(0);
  const [postBreakdown, setPostBreakdown] = useState<BreakdownItem[]>([]);
  const [providerBreakdown, setProviderBreakdown] = useState<BreakdownItem[]>([]);
  const [engineBreakdown, setEngineBreakdown] = useState<BreakdownItem[]>([]);
  const [socialBreakdown, setSocialBreakdown] = useState<BreakdownItem[]>([]);
  const [countryBreakdown, setCountryBreakdown] = useState<BreakdownItem[]>([]);
  const [browserBreakdown, setBrowserBreakdown] = useState<BreakdownItem[]>([]);
  const [deviceBreakdown, setDeviceBreakdown] = useState<BreakdownItem[]>([]);
  const [referrerBreakdown, setReferrerBreakdown] = useState<BreakdownItem[]>([]);
  const [retention, setRetention] = useState<BreakdownItem[]>([]);
  const [realtime, setRealtime] = useState<RealtimeStats>({ activeNow: 0, eventsLastHour: 0, emailsToday: 0, totalVisits: 0, liveBreakdown: [], messages24h: 0 });
  const [blogStats, setBlogStats] = useState<BlogStats>({ total: 0, published: 0, draft: 0, impressions: 0, organic_clicks: 0 });
  const [blogReferrers, setBlogReferrers] = useState<BreakdownItem[]>([]);
  const [blogEngines, setBlogEngines] = useState<BreakdownItem[]>([]);
  const [socialDetail, setSocialDetail] = useState<SocialDetailBundle | null>(null);

  const getCustomTs = () => ({
    from: fromDatetimeLocal(customFrom),
    to: fromDatetimeLocal(customTo),
  });

  const loadAll = useCallback(async () => {
    setLoading(true);
    const cf = range === 'custom' ? getCustomTs() : undefined;
    const [b, sd] = await Promise.all([
      fetchAnalyticsBundle(type, range, activeWindow, 24, cf),
      fetchSocialDetail(range, cf),
    ]);
    setSeries(b.series); setUniqueSeries(b.uniqueSeries); setTotalUnique(b.totalUnique);
    setRealtime(b.realtime);
    const bd = b.breakdowns;
    setPostBreakdown(bd.posts);       setProviderBreakdown(bd.providers);
    setEngineBreakdown(bd.engines);   setSocialBreakdown(bd.social);
    setCountryBreakdown(bd.countries); setBrowserBreakdown(bd.browsers);
    setDeviceBreakdown(bd.devices);   setReferrerBreakdown(bd.referrers);
    setRetention(bd.retention);
    setBlogStats(b.blogStats); setBlogReferrers(b.blogReferrers); setBlogEngines(b.blogEngines);
    setSocialDetail(sd);
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, range, activeWindow, customFrom, customTo]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Persist filter selections
  const updateRange = (r: string) => { setRange(r); localStorage.setItem('admin_analytics_range', r); };
  const updateActiveWindow = (w: string) => { setActiveWindow(w); localStorage.setItem('admin_analytics_window', w); };
  const updateType = (t: typeof type) => { setType(t); localStorage.setItem('admin_analytics_type', t); };

  // One-shot stats refresh on mount / filter change. Deliberately no polling:
  // the admin panel is reload-based, and /api/admin/stats is expensive on D1.
  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchAdminStats();
        setRealtime(prev => ({
          ...prev,
          eventsLastHour: data.eventsLastHour,
          emailsToday: data.emailsToday,
          totalVisits: data.totalVisits,
          messages24h: data.messages24h,
        }));
        fetchRealtimeStats(activeWindow).then(rt => {
          setRealtime(prev => ({ ...prev, activeNow: rt.activeNow }));
        }).catch(() => {});
      } catch { /* ignore */ }
    };
    void load();
  }, [activeWindow]);

  const rangeLabel = RANGE_CONFIG.find(r => r.key === range)?.label || range;

  const chartData = {
    labels: series.map(p => formatBucketLabel(p.bucketStart, range)),
    datasets: [{ label: `${type} events`, data: series.map(p => p.count), backgroundColor: 'rgba(59,130,246,0.6)', borderColor: 'rgba(59,130,246,1)', borderWidth: 1 }],
  };
  const uniqueChartData = {
    labels: uniqueSeries.map(p => formatBucketLabel(p.bucketStart, range)),
    datasets: [{ label: 'Unique Visitors', data: uniqueSeries.map(p => p.count), fill: true, backgroundColor: 'rgba(16,185,129,0.15)', borderColor: 'rgba(16,185,129,1)', borderWidth: 2, pointRadius: 3, tension: 0.4 }],
  };
  const chartOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true } } };
  const lineOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true } } };

  const exportHref = buildExportUrl(range, type !== 'visit' ? type : undefined, range === 'custom' ? getCustomTs() : undefined);
  const hasData = series.some(p => p.count > 0);

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
          Analytics
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <a href={exportHref} download className="flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition">
            ⬇️ CSV
          </a>
          <button onClick={loadAll} disabled={loading} className="bg-blue-600 text-white rounded-xl px-4 py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition">
            {loading ? '…' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {/* Active users card + window selector */}
      <div className="bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-700 rounded-2xl p-4 mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse" />
          <div>
            <span className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">{realtime.activeNow}</span>
            <span className="ml-2 text-sm font-medium text-emerald-700 dark:text-emerald-400">live users</span>
          </div>
          <span className="text-xs text-emerald-600 dark:text-emerald-500 bg-emerald-100 dark:bg-emerald-800/50 px-2 py-0.5 rounded-full">updates on reload</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Window:</span>
          {ACTIVE_WINDOWS.map(w => (
            <button
              key={w.key}
              onClick={() => updateActiveWindow(w.key)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium transition ${activeWindow === w.key ? 'bg-emerald-600 text-white' : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50'}`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {/* Event type selector */}
      <div className="mb-4">
        <div className="flex flex-wrap gap-2">
          {(['visit', 'email', 'post_visit'] as const).map(t => (
            <button key={t} onClick={() => updateType(t)}
              className={`px-3 py-1.5 rounded-xl text-sm font-medium transition border ${type === t ? 'bg-blue-600 text-white border-blue-600 shadow' : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50'}`}>
              {t === 'visit' ? '🌐 Visits' : t === 'email' ? '📬 Inboxes' : '📝 Blog Views'}
            </button>
          ))}
        </div>
      </div>

      {/* Time range selector */}
      <div className="mb-4">
        <div className="flex flex-wrap gap-1.5">
          {RANGE_CONFIG.map(r => (
            <button
              key={r.key}
              onClick={() => updateRange(r.key)}
              className={`px-3 py-1.5 rounded-xl text-xs font-medium transition ${range === r.key ? 'bg-blue-600 text-white shadow-md' : 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {/* Custom date range pickers */}
        {range === 'custom' && (
          <div className="mt-3 flex flex-wrap items-center gap-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl px-4 py-3">
            <span className="text-xs font-medium text-blue-700 dark:text-blue-300">From:</span>
            <input
              type="datetime-local"
              value={customFrom}
              onChange={e => setCustomFrom(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-xs font-medium text-blue-700 dark:text-blue-300">To:</span>
            <input
              type="datetime-local"
              value={customTo}
              onChange={e => setCustomTo(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button onClick={loadAll} disabled={loading} className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition">
              {loading ? '…' : 'Apply'}
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-500">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          <p className="mt-4 text-sm">Loading analytics…</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Quick stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard label={`Live (${ACTIVE_WINDOWS.find(w => w.key === activeWindow)?.label})`} value={realtime.activeNow} sub="active visitors" accent="ring-emerald-300" icon="🟢" />
            <StatCard label="Messages Received (24h)" value={realtime.messages24h} icon="📥" />
            <StatCard label="Inboxes Created (24h)" value={realtime.emailsToday} icon="📬" />
            <StatCard label="Unique Visitors" value={totalUnique} sub={`in ${rangeLabel}`} icon="🙋" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <StatCard label="Total Visits (all time)" value={realtime.totalVisits} icon="👁️" />
            <StatCard label="Top Search Engine" value={engineBreakdown[0]?.label || '—'} sub={engineBreakdown[0] ? `${engineBreakdown[0].count.toLocaleString()} visits` : undefined} icon="🔍" />
            <StatCard label="Top Social" value={socialBreakdown[0]?.label || '—'} sub={socialBreakdown[0] ? `${socialBreakdown[0].count.toLocaleString()} visits` : undefined} icon="📱" />
          </div>

          {/* No data hint */}
          {!hasData && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-2xl px-5 py-4 text-sm text-amber-700 dark:text-amber-400 flex items-center gap-3">
              <span className="text-xl">💡</span>
              <div>
                <p className="font-semibold">No {type} events in the selected time range ({rangeLabel})</p>
                <p className="text-xs mt-0.5 opacity-80">Try a wider range like "7 days" or "All time" to see historical data.</p>
              </div>
            </div>
          )}

          {/* Main event chart */}
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-lg border border-gray-200/60 dark:border-gray-700/60 p-6" style={{ height: 320 }}>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-4 uppercase tracking-wide">
              {type} events over time — {rangeLabel}
            </p>
            <Bar data={chartData} options={chartOptions} />
          </div>

          {/* Unique visitors chart */}
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-lg border border-gray-200/60 dark:border-gray-700/60 p-6" style={{ height: 280 }}>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-4 uppercase tracking-wide">
              Unique visitors over time
            </p>
            <Line data={uniqueChartData} options={lineOptions} />
          </div>

          {/* Breakdown sections — load after charts */}
          {false ? null : (
            <>
          {/* Retention */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <BreakdownList title="🔄 New vs Returning Visitors" items={retention} emptyMsg="No visitor data in this range." colorMap={RETENTION_COLORS} />
            <DonutCard title="🔄 Retention Split" items={retention} colorMap={RETENTION_COLORS} />
          </div>

          {/* Search Engines */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <BreakdownList title="🔍 Search Engine Traffic" items={engineBreakdown} emptyMsg="No search engine data." colorMap={ENGINE_COLORS} />
            <DonutCard title="🔍 Engine Share" items={engineBreakdown} colorMap={ENGINE_COLORS} />
          </div>

          {/* Social Media Deep Dive */}
          <SocialDeepDive detail={socialDetail} socialBreakdown={socialBreakdown} range={range} />

          {/* Referrers */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <BreakdownList title="🔗 Top Referrers" items={referrerBreakdown} emptyMsg="No referrer data." maxItems={12} />
            <DonutCard
              title="🔗 Referrer Share"
              items={referrerBreakdown.slice(0, 8)}
              colorMap={Object.fromEntries(referrerBreakdown.map((i, idx) => [i.label, `hsl(${idx * 37}, 65%, 55%)`]))}
            />
          </div>

          {/* Country */}
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-lg border border-gray-200/60 dark:border-gray-700/60 p-6">
            <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-4 uppercase tracking-wide">🌍 Traffic by Country</h2>
            {countryBreakdown.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No country data in this range.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {countryBreakdown.map(item => {
                  const total = countryBreakdown.reduce((s, i) => s + i.count, 0);
                  const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
                  return (
                    <div key={item.label} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/40 rounded-xl px-4 py-2.5">
                      <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">{item.label}</span>
                      <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
                        {item.count.toLocaleString()} <span className="text-gray-400 font-normal text-xs">({pct}%)</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Browser + Device */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <BreakdownList title="🌐 Browser Breakdown" items={browserBreakdown} emptyMsg="No browser data." colorMap={BROWSER_COLORS} />
            <DonutCard title="🌐 Browser Share" items={browserBreakdown} colorMap={BROWSER_COLORS} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <BreakdownList title="📱 Device Types" items={deviceBreakdown} emptyMsg="No device data." colorMap={DEVICE_COLORS} />
            <DonutCard title="📱 Device Split" items={deviceBreakdown} colorMap={DEVICE_COLORS} />
          </div>

          {/* ── Blog Performance section ── */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border border-purple-200/60 dark:border-purple-700/60 rounded-2xl p-5">
            <h2 className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
              📝 Blog & Guides Performance
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              <StatCard label="Total Blog Posts"   value={blogStats.total}          icon="📄" />
              <StatCard label="Published"          value={blogStats.published}       icon="✅" accent="ring-emerald-300 dark:ring-emerald-700" />
              <StatCard label="Total Impressions"  value={blogStats.impressions}     icon="👁️" sub={`in ${rangeLabel}`} />
              <StatCard label="Organic Clicks"     value={blogStats.organic_clicks}  icon="🔍" sub="from search engines" accent="ring-blue-300 dark:ring-blue-700" />
            </div>

            {/* CTR banner */}
            {blogStats.impressions > 0 && (
              <div className="mb-4 bg-white/70 dark:bg-gray-800/60 rounded-xl px-4 py-3 text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <span className="text-lg">🎯</span>
                <span>
                  <span className="font-bold text-purple-700 dark:text-purple-300">
                    {blogStats.impressions > 0 ? Math.round((blogStats.organic_clicks / blogStats.impressions) * 100) : 0}% organic CTR
                  </span>
                  {' '}— {blogStats.organic_clicks.toLocaleString()} clicks from search out of {blogStats.impressions.toLocaleString()} impressions
                </span>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <BreakdownList title="🔍 Organic Search to Blog" items={blogEngines} emptyMsg="No search engine traffic to blog posts yet." />
              <BreakdownList title="🔗 Where Blog Readers Come From" items={blogReferrers} emptyMsg="No referrer data for blog posts yet." maxItems={10} />
            </div>
          </div>

          {/* Blog + Providers */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <BreakdownList title="📝 Top Blog Posts" items={postBreakdown} emptyMsg="No blog post views in this range." />
            <BreakdownList title="📬 Inbox Provider Mix" items={providerBreakdown} emptyMsg="No inboxes created in this range." />
          </div>
            </>
          )}

          {/* Download options */}
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-lg border border-gray-200/60 dark:border-gray-700/60 p-6">
            <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-4 uppercase tracking-wide">⬇️ Download Reports</h2>
            <div className="flex flex-wrap gap-2">
              {(['visit', 'email', 'post_visit'] as const).map(t => (
                <a
                  key={t}
                  href={buildExportUrl(range, t, range === 'custom' ? getCustomTs() : undefined)}
                  download
                  className="px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition font-medium"
                >
                  {t === 'visit' ? '🌐 Visits' : t === 'email' ? '📬 Inboxes' : '📝 Blog'} CSV
                </a>
              ))}
              <a href={buildExportUrl('all')} download className="px-3 py-2 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-xl text-xs text-blue-700 dark:text-blue-300 hover:bg-blue-100 transition font-medium">
                📦 All Events CSV
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminAnalytics;
